import { GOOGLE_CLASSROOM_MIDLEVEL } from './../../shared/constants/google-classroom.constant';
import { REMOTE_INTERACTION_MIDLEVEL } from './../../shared/constants/remote-interaction.constant';
import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IStateParamsService, IStateService } from 'angular-ui-router';
import { each, find, result } from 'lodash';
import { Channel } from 'pusher-js/with-encryption';
import { BehaviorSubject, Subscription } from 'rxjs';
import { flatMap, switchMap, take, tap } from 'rxjs/operators';
import { Toggles } from './../../shared/constants/toggles.constant';
import { ToggleService } from './../../shared/services/toggle/toggle.service';
import { IUser } from './../../shared/typings/interfaces/user.interface';
import { ResetFocus } from './../../store/actions/focus-actions/focus-actions';
import { unsubscribeComponent } from '../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { UtilitiesService } from '../../shared/services/utilities/utilities.service';
import { ResetRollupGrouping } from '../../store/actions/rollup-grouping-actions/rollup-grouping-actions';
import { IFocus } from '../tiles/interfaces/dashboard.interfaces';
import { SpinnerModal } from './../../../app/shared/modals/status-spinner/status-spinner.service';
import { NYCT_DASH_MIDLEVEL } from './../../shared/constants/nyct-dash.constant';
import { FocusDataService } from './mid-level-dash-data/focus-data/focus-data.service';
import { RollupGroupingDataService } from './mid-level-dash-data/rollup-grouping-data/rollup-grouping-data.service';
import { TValidMadlibDimension } from './viz-mad-lib/viz-mad-lib.component';
import { IOptions } from './viz-mad-lib/viz-mad-lib.service';
import { ISchool } from './../../shared/typings/interfaces/school.interface';

export interface IOptions {
  graphQlKey: string;
  parent: string;
  parentOrder: number;
  children: Array<{
    graphQlKey: string;
    child: string;
    childOrder: number;
  }>;
}

interface IMadlibSelections {
  focus?: string;
  filter?: string;
  grouping?: string;
}

export interface IRow {
  data: string | number;
  meta: any;
  columnKey: string;
  tooltip?: string;
}

export interface IBar {
  group: string;
  value: number;
}

export interface IMadlibFormValue {
  [ctrlName: string]: {
    graphQlKey: string;
    human: string;
  };
}

export type TValidMadlibDimension = 'focus' | 'filter' | 'grouping';

export interface IMadlibFormInfo {
  form: IMadlibFormValue;
  dim: TValidMadlibDimension;
}

@Component({
  selector: 'mid-level-dash',
  template: require('./mid-level-dash.component.html'),
  styles: [require('./mid-level-dash.component.scss')],
})
@unsubscribeComponent
export class MidLevelDashComponent implements OnInit {
  @ViewChild('dashContainer') dashContainer: ElementRef;
  @Input() currentSchool: ISchool;
  @Input() hoveredTableRow: IRow;
  @Input() channel: Channel;
  @Input() currentUser: IUser;

  hoveredBar: string;
  setPanelShadowStyle: boolean = false;
  listColumns: IFocus['midLevelColumns'];
  defaultFilter: { graphQlKey: string };
  defaultGrouping: { graphQlKey: string };
  focusOptions: IFocus['focusOptions'];
  filterOptions: IFocus['filterOptions'];
  groupingOptions: IFocus['midLevelGroupingOptions'];
  studentGrouping: IFocus['studentLevelGroupingOptions'];
  populationFilterKey: string;
  madlibDimension: TValidMadlibDimension;
  focusDataSub: Subscription;
  currentMadlibSub: Subscription;

  madlibData: Array<{ options: IOptions[]; type: string }>;
  madlibForm: FormGroup;
  focusStatusSub: Subscription;
  focusChanged: boolean = false;
  defaultMadlibs: IMadlibSelections;
  rollupGrouping$ = new BehaviorSubject(null);
  focusData$ = new BehaviorSubject(null);

  isNYCTToggleOn: boolean = false;
  isRemoteInteractionToggleOn: boolean = false;
  isGoogleClassroomToggleOn: boolean = false;

  constructor(
    @Inject('$state')
    public $state: IStateService,
    @Inject('$stateParams')
    private $stateParams: IStateParamsService,
    private utilitiesService: UtilitiesService,
    private store: Store<any>,
    private focusDataService: FocusDataService,
    private rollupGroupingDataService: RollupGroupingDataService,
    @Inject('SpinnerModal') private spinnerModal: SpinnerModal,
    private toggleService: ToggleService,
  ) {}

  ngOnInit() {
    this.spinnerModal.activateSpinner('Loading...');
    this.initializeMidlevelDash(this.$stateParams);
    this.isNYCTToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
    this.isRemoteInteractionToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_REMOTE_INTERACTION);
    this.isGoogleClassroomToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_GOOGLE_CLASSROOM);
  }

  getMadlibDropdownOptions(focusData): Array<{ options: IOptions[]; type: string }> {
    // Remove NYCT focus options if not NYCT school
    if (!this.isNYCTToggleOn) {
      let academicFocus = focusData.focusOptions.find(({ parent }) => parent === 'Academic');
      if (academicFocus) {
        academicFocus.children = academicFocus.children.filter(({ graphQlKey }) => !NYCT_DASH_MIDLEVEL[graphQlKey]);
      }
    }
    // Remove Remote Interaction focus options if not NV Charter school
    if (!this.isRemoteInteractionToggleOn) {
      let remoteLearningFocus = focusData.focusOptions.find(({ parent }) => parent === 'Remote Learning');
      if (remoteLearningFocus) {
        remoteLearningFocus.children = remoteLearningFocus.children.filter(
          ({ graphQlKey }) => !REMOTE_INTERACTION_MIDLEVEL[graphQlKey],
        );
      }
    }
    // Remove Remote Interaction focus options if not NV Affinity school
    if (!this.isGoogleClassroomToggleOn) {
      let remoteLearningFocus = focusData.focusOptions.find(({ parent }) => parent === 'Remote Learning');
      if (remoteLearningFocus) {
        remoteLearningFocus.children = remoteLearningFocus.children.filter(
          ({ graphQlKey }) => !GOOGLE_CLASSROOM_MIDLEVEL[graphQlKey],
        );
      }
    }
    // Remove Gradebook focus options if not NV Affinity school
    if (this.currentSchool.isNVCMO) {
      let remoteLearningFocus = focusData.focusOptions.find(({ parent }) => parent === 'Remote Learning: Graduation');
      if (remoteLearningFocus) {
        remoteLearningFocus.children = remoteLearningFocus.children.filter(({ graphQlKey }) => {
          return graphQlKey !== 'NUMBER_NOT_PASSING_NEEDED_FOR_EXEMPTION_GB_SOURCE';
        });
      }
    }

    const filterOptions = focusData.filterOptions;
    const groupingOptions = focusData.midLevelGroupingOptions;
    const focusOptions = focusData.focusOptions;
    return [
      { options: focusOptions, type: 'focus' },
      { options: filterOptions, type: 'filter' },
      { options: groupingOptions, type: 'grouping' },
    ];
  }

  onFreshData(): void {
    this.initializeMidlevelDash(this.$stateParams);
  }

  getMadlibPatch(focusData: IFocus, initialMadlibValues: IMadlibSelections) {
    const { focus, filter, grouping } = initialMadlibValues;
    const filterPatch = filter || focusData.defaultFilter.graphQlKey;
    const groupingPatch = grouping || focusData.defaultMidLevelGrouping.graphQlKey;
    return {
      focus: {
        graphQlKey: focus,
        human: this.getHumanKey(this.focusOptions, focus),
      },
      filter: {
        graphQlKey: filterPatch,
        human: this.getHumanKey(this.filterOptions, filterPatch),
      },
      grouping: {
        graphQlKey: groupingPatch,
        human: this.getHumanKey(this.groupingOptions, groupingPatch),
      },
    };
  }

  initializeMidlevelDash(initialMadlibValues: IMadlibSelections) {
    const { focus, filter: tileFilter } = initialMadlibValues;

    this.store.dispatch(new ResetFocus());
    this.store.dispatch(new ResetRollupGrouping());

    // subscription managed by take(1)
    this.focusDataService
      .getFocusData$({ schoolId: this.currentSchool._id, focusKey: focus })
      .pipe(
        tap((focusData: IFocus) => {
          // set shared props
          this.focusData$.next(focusData);
          this.filterOptions = focusData.filterOptions;
          this.groupingOptions = focusData.midLevelGroupingOptions;
          this.focusOptions = focusData.focusOptions;
          this.studentGrouping = focusData.studentLevelGroupingOptions;
          this.listColumns = focusData.midLevelColumns;
          this.populationFilterKey = focusData.graphQlKeyPopulationFilter;
          this.defaultFilter = focusData.defaultFilter;
          this.defaultGrouping = focusData.defaultMidLevelGrouping;
          this.madlibData = this.getMadlibDropdownOptions(focusData);
          // create a form
          this.madlibForm = this.madlibForm ? this.madlibForm : this.createMadlibForm();
          // patch form
          const madlibPatch = this.getMadlibPatch(focusData, initialMadlibValues);
          this.madlibForm.patchValue(madlibPatch);
          // update url
          this.updateUrl(this.madlibForm.value);
        }),
        switchMap(focusData =>
          this.rollupGroupingDataService.getRollupGroupingPayload(focusData, this.currentSchool._id, tileFilter),
        ),
        flatMap(payload => this.rollupGroupingDataService.getRollupData$(payload)),
        tap(rollupGrouping => this.rollupGrouping$.next(rollupGrouping)),
        tap(() => this.spinnerModal.deactivateSpinner()),
        take(1),
      )
      .subscribe();
  }

  createMadlibForm(): FormGroup {
    const madlibPills = ['focus', 'filter', 'grouping'];
    const pillControls = madlibPills.reduce((accum, madlibPill: 'focus' | 'filter' | 'grouping') => {
      accum[madlibPill] = new FormControl({ graphQlKey: null, human: null });
      return accum;
    }, {});
    return new FormGroup(pillControls);
  }

  handleMadlibChange(event$: {
    dimension: TValidMadlibDimension;
    option: { graphQlKey: string; parent: string; child: string };
  }): void {
    const { dimension, option } = event$;
    if (dimension === 'focus') this.onFocusChange(option);
    else this.onFilterOrGroupingChange(dimension, option);
  }

  onFocusChange(option: { graphQlKey: string }): void {
    const { graphQlKey: focus } = option;
    this.initializeMidlevelDash({ focus });
    this.focusChanged = true;
  }

  onFilterOrGroupingChange(dimension: string, option: { graphQlKey: string; parent: string; child: string }): void {
    const { graphQlKey: updatedDimensionKey, child, parent } = option;
    const human = child ? child : parent;
    const patch = { [dimension]: { graphQlKey: updatedDimensionKey, human } };

    this.madlibForm.patchValue(patch);

    const {
      filter: { graphQlKey: filterKey },
      grouping: { graphQlKey: groupingKey },
    } = this.madlibForm.value;

    const payload = {
      schoolId: this.currentSchool._id,
      filterKeys: [filterKey, this.populationFilterKey],
      groupingKey,
      columns: this.listColumns.map(col => col.graphQlKey),
    };

    this.store.dispatch(new ResetRollupGrouping());

    this.rollupGroupingDataService
      .getRollupData$(payload)
      .pipe(
        tap(groupingData => this.rollupGrouping$.next(groupingData)),
        take(1),
      )
      .subscribe();

    this.updateUrl(this.madlibForm.value);
    this.focusChanged = false;
  }

  updateUrl(madlibForm: { [key: string]: { graphQlKey: string } }): void {
    const {
      focus: { graphQlKey: focusKey },
      filter: { graphQlKey: filterKey },
      grouping: { graphQlKey: groupingKey },
    } = madlibForm;

    this.$state.go(
      '.',
      { focus: focusKey, filter: filterKey, grouping: groupingKey },
      { notify: false, location: 'replace' },
    );
  }

  shareHoveredTableRow(row: IRow): void {
    this.hoveredTableRow = row;
  }

  shareHoveredBar(barGroup: IBar['group']): void {
    this.hoveredBar = barGroup || null;
  }

  goToDashboard(): void {
    this.$state.go('main.school.lists.tiles');
  }

  goToStudentLevelWithRow(row: IRow[], madlibForm): void {
    const stubColumn = row[0];
    const {
      focus: { graphQlKey: focusKey },
      filter: { graphQlKey: filterKey },
      grouping: { graphQlKey: groupingKey },
    } = madlibForm;
    const rowFilter = stubColumn.meta.data;
    // Note-jchu:
    // 1. when rowFilter is null, student level shows filtered data whose data point is null, and data col is displayed as 'N/A, None, or...'
    // 2. when rowFilter is '2022' or such and such, student level shows filtered data whose data point is NOT null
    // 3. when rowFilter is 'All', student level shows all data without filtering
    const isFooterRow = rowFilter === 'All';
    const rowFilterKey = isFooterRow
      ? null
      : typeof rowFilter === 'string'
      ? `${groupingKey}=\\"${rowFilter}\\"`
      : `${groupingKey}=null`;
    this.$state.go('main.school.lists.studentLevel', {
      focus: focusKey,
      filter: filterKey,
      grouping: groupingKey,
      studentFilter: rowFilterKey,
      studentGrouping: this.studentGrouping[0].graphQlKey,
      rowStub: stubColumn.data,
    });
  }

  onListScroll(): void {
    this.setPanelShadowStyle = this.utilitiesService.showListPanelShadow(this.dashContainer.nativeElement.scrollTop);
  }

  // TODO: rework configs/logic to no longer need this functionality
  getHumanKey(options: IOptions[], key: string): string {
    let match: string;
    match = result(find(options, (x: IOptions) => x.graphQlKey === key), 'parent');
    if (!match) {
      each(options, (option: IOptions) => {
        let suboptionMatch = find(option.children, (x: { graphQlKey: string }) => x.graphQlKey === key);
        if (suboptionMatch) match = result(suboptionMatch, 'child');
      });
    }
    return match;
  }
}
